import { FieldPath, FieldValues, UseControllerProps, useController } from "react-hook-form";
import { FormFieldError } from "../FormFieldError";
import { DropdownInput, DropdownInputProps } from "../inputs/DropdownInput";

export type FormDropdownProps<
	TFieldValues extends FieldValues,
	TTransformedValues = TFieldValues,
> = UseControllerProps<TFieldValues, FieldPath<TFieldValues>, TTransformedValues> &
	Omit<DropdownInputProps, "value" | "onChange" | "error"> & {
		placeholder?: string;
		disabled?: boolean;
		readonly?: boolean;
		errorMessage?: string;
		hideErrorMessage?: boolean;
	};

export const FormDropdown = <TFieldValues extends FieldValues, TTransformedValues = TFieldValues>({
	name,
	control,
	defaultValue,
	disabled,
	readonly,
	errorMessage,
	hideErrorMessage = false,
	include: _include,
	...dropdownProps
}: FormDropdownProps<TFieldValues, TTransformedValues> & { include?: boolean }) => {
	const {
		field: { ref: _ref, onBlur, onChange, ...field },
		fieldState: { error },
	} = useController<TFieldValues, FieldPath<TFieldValues>, TTransformedValues>({
		name,
		control,
		defaultValue,
	});

	const hasError = !!error;

	const handleChange = (value: string) => {
		onChange(value);
		onBlur();
	};

	return (
		<div className="relative">
			<DropdownInput
				{...dropdownProps}
				{...field}
				onChange={handleChange}
				disabled={disabled}
				readonly={readonly}
				error={hasError}
			/>
			{hasError && !hideErrorMessage && (
				<FormFieldError error={error} errorMessage={errorMessage} />
			)}
		</div>
	);
};
